import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, ViewEncapsulation } from '@angular/core';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { Marked, Renderer, RendererObject, Token, Tokens, TokensList } from 'marked';

const baseRenderer: RendererObject = {
  table(token) {
    const table = Renderer.prototype.table.call(this, token)
      .replace('<table>', '<table class="br-table">');
    return `<div class="br-table-responsive br-focus-ring" tabindex="0" role="region" aria-label="Table">${table}</div>`;
  },
  link(token) {
    return Renderer.prototype.link.call(this, token)
      .replace('<a ', '<a target="_blank" rel="noopener noreferrer" class="br-focus-ring" ');
  },
};
const markdown = new Marked({ gfm: true, breaks: true, renderer: baseRenderer });

// Report mode shares the chat typography but renders every raw HTML token — block, inline and nested — as
// literal escaped text, so an author's markup (classes, layout, <script>, <style>, event handlers) can never
// become active DOM. Chat keeps its existing raw-HTML-then-Angular-sanitization behaviour unchanged.
const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const reportMarkdown = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    ...baseRenderer,
    html(token: Tokens.HTML | Tokens.Tag) { return escapeHtml(token.text); },
  },
});

/** Report charts carry native Chart.js data only; the product supplies every style and option. */
const reportChartTypes = ['bar', 'line', 'pie', 'doughnut'] as const;
type ReportChartType = (typeof reportChartTypes)[number];
const maxCharts = 12, maxLabels = 60, maxDatasets = 6, numberBound = 1e12;

type Segment =
  | { kind: 'html'; html: string }
  | { kind: 'chart'; type: ReportChartType; data: ChartData; options: ChartOptions; label: string }
  | { kind: 'error'; message: string };

@Component({
  selector: 'app-chat-markdown',
  standalone: true,
  imports: [CommonModule, NgChartsModule],
  templateUrl: './chat-markdown.component.html',
  styleUrls: ['./chat-markdown.component.css'],
  // Parsed elements have no Angular scope attribute; every rule is host-prefixed.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMarkdownComponent {
  /** Chat renders one sanitized HTML fragment; a report additionally renders native chart blocks. */
  html = '';
  segments: Segment[] = [];
  private raw = '';
  private renderCharts = false;
  private shownTitle = '';

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  @Input({ required: true }) set text(value: string) {
    // Angular updates this input only when the raw text changes, including SSE
    // deltas. Keep a plain string so [innerHTML] applies Angular's sanitization.
    this.raw = value;
    this.render();
  }

  /** Opt-in: render fenced ```chart blocks as charts and the rest as Markdown. Chat callers leave this off. */
  @Input() set charts(value: boolean) {
    this.renderCharts = value;
    this.render();
  }
  get charts(): boolean { return this.renderCharts; }

  /** Report cards and detail headers already show this title; keep the stored document intact. */
  @Input() set titleAlreadyShown(value: string | null | undefined) {
    this.shownTitle = value ?? '';
    this.render();
  }

  readonly trackSegment = (index: number) => index;

  private render(): void {
    if (!this.renderCharts) { this.html = markdown.parse(this.raw, { async: false }); return; }
    // The lexer resolves inline tokens (including reference links anywhere in the document) in one pass,
    // so splitting the block tokens afterwards keeps links around charts working. Chart-looking text inside
    // an ordinary fenced code block stays a literal code token, never a chart.
    const tokens = reportMarkdown.lexer(this.raw);
    const first = tokens.findIndex(token => token.type !== 'space');
    if (this.shownTitle && tokens[first]?.type === 'heading') {
      // Compare visible text so escaped punctuation, entities and emphasis still match.
      // A template is inert; reportMarkdown escapes raw HTML before parsing it here.
      const heading = this.document.createElement('template');
      heading.innerHTML = reportMarkdown.parser([tokens[first]]);
      const normalize = (text: string) => text.replace(/\s+/g, ' ').trim();
      if (normalize(heading.content.textContent ?? '') === normalize(this.shownTitle)) tokens.splice(first, 1);
    }
    const segments: Segment[] = [];
    let group: Token[] = [];
    let charts = 0;
    const flush = () => {
      if (!group.length) return;
      // Reference-link definitions are resolved during lexing and kept on tokens.links; carry them so
      // links that sit around a chart block still render when the group is parsed on its own.
      (group as unknown as TokensList).links = tokens.links;
      segments.push({ kind: 'html', html: reportMarkdown.parser(group) });
      group = [];
    };
    for (const token of tokens) {
      if (token.type === 'code' && (token.lang ?? '').trim().split(/\s+/)[0].toLowerCase() === 'chart') {
        flush();
        charts++;
        segments.push(charts > maxCharts
          ? { kind: 'error', message: `A report may contain at most ${maxCharts} charts.` }
          : this.buildChart(token.text));
      } else {
        group.push(token);
      }
    }
    flush();
    this.segments = segments;
  }

  /** Validates and renders one native Chart.js data block; a failure shows a visible notice, never a silent drop. */
  private buildChart(json: string): Segment {
    let parsed: unknown;
    try { parsed = JSON.parse(json); } catch { return this.chartError('is not valid JSON'); }
    if (!parsed || typeof parsed !== 'object') return this.chartError('must be a JSON object');
    const chart = parsed as Record<string, unknown>;
    for (const key of Object.keys(chart)) if (key !== 'type' && key !== 'data') return this.chartError(`has an unsupported property "${key}"`);
    const type = chart['type'];
    if (typeof type !== 'string' || !reportChartTypes.includes(type as ReportChartType)) return this.chartError('needs a type of bar, line, pie or doughnut');
    const data = chart['data'];
    if (!data || typeof data !== 'object') return this.chartError('needs a data object with labels and datasets');
    for (const key of Object.keys(data)) if (key !== 'labels' && key !== 'datasets') return this.chartError(`data has an unsupported property "${key}"`);
    const labels = (data as Record<string, unknown>)['labels'];
    if (!Array.isArray(labels) || labels.length < 1 || labels.length > maxLabels || !labels.every(l => typeof l === 'string' && l.length <= 80))
      return this.chartError('needs 1 to 60 text labels');
    const datasets = (data as Record<string, unknown>)['datasets'];
    if (!Array.isArray(datasets) || datasets.length < 1 || datasets.length > maxDatasets) return this.chartError('needs 1 to 6 datasets');
    // Legible, high-contrast theme tokens only (no pale -soft/border background tokens), and no red/green
    // categorical pairing. Line series are additionally distinguished by dash and point shape, so they stay
    // readable without relying on colour alone.
    const stroke = ['primary', 'secondary', 'text', 'muted'].map(name => this.color(name));
    const dashes = [[], [6, 4], [2, 3], [8, 4, 2, 4]];
    const pointStyles = ['circle', 'rect', 'triangle', 'rectRot', 'star', 'crossRot'];
    const circular = type === 'pie' || type === 'doughnut';
    const clean: { label: string; data: number[]; backgroundColor: string | string[]; borderColor: string | string[]; borderWidth: number; borderDash: number[]; pointStyle: string; pointRadius?: number; pointBackgroundColor: string; pointBorderColor: string; pointHoverBackgroundColor: string; pointHoverBorderColor: string }[] = [];
    for (let i = 0; i < datasets.length; i++) {
      const dataset = datasets[i] as Record<string, unknown>;
      if (!dataset || typeof dataset !== 'object') return this.chartError('has a malformed dataset');
      for (const key of Object.keys(dataset)) if (key !== 'label' && key !== 'data') return this.chartError(`dataset has an unsupported property "${key}"`);
      const label = dataset['label'];
      if (typeof label !== 'string' || !label || label.length > 80) return this.chartError('has a dataset without a label');
      const values = dataset['data'];
      if (!Array.isArray(values) || values.length !== labels.length
        || !values.every(v => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= numberBound))
        return this.chartError('needs one finite number per label in every dataset');
      const base = stroke[i % stroke.length];
      clean.push({
        label, data: values as number[],
        backgroundColor: circular ? (labels as string[]).map((_, j) => stroke[j % stroke.length]) : base,
        borderColor: circular ? this.color('surface') : base,
        borderWidth: type === 'bar' ? 0 : circular ? 1 : 2,
        borderDash: type === 'line' ? dashes[i % dashes.length] : [],
        pointStyle: pointStyles[i % pointStyles.length],
        pointRadius: circular ? undefined : 3,
        // ng2-charts supplies its own point palette; explicitly keep points and hover states
        // on the same product colour as this dataset's line.
        pointBackgroundColor: base,
        pointBorderColor: base,
        pointHoverBackgroundColor: base,
        pointHoverBorderColor: base,
      });
    }
    const names = clean.map(d => d.label).join(', ');
    const categories = (labels as string[]).join(', ');
    const summary = `${type} chart with ${clean.length} series (${names}) across ${(labels as string[]).length} categories: ${categories}.`;
    return { kind: 'chart', type: type as ReportChartType,
      data: { labels: labels as string[], datasets: clean } as ChartData, options: this.chartOptions(type as ReportChartType), label: summary };
  }

  private chartError(reason: string): Segment { return { kind: 'error', message: `A chart could not be shown: it ${reason}.` }; }

  private color(name: string): string {
    return getComputedStyle(this.document.documentElement).getPropertyValue('--br-color-' + name).trim() || '#888888';
  }

  private chartOptions(type: ReportChartType): ChartOptions {
    const options: ChartOptions = {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { position: 'bottom', labels: { color: this.color('text'), usePointStyle: type === 'line', boxWidth: type === 'line' ? 8 : 14, boxHeight: 8 } } },
    };
    if (type === 'bar') options.elements = { bar: { borderWidth: 0, hoverBorderWidth: 0 } };
    if (type !== 'pie' && type !== 'doughnut') {
      const axis = { ticks: { color: this.color('muted') }, grid: { color: this.color('border-soft') } };
      options.scales = { x: axis, y: { ...axis, beginAtZero: true } } as ChartOptions['scales'];
    }
    return options;
  }
}
